import React, { useState, useEffect, useMemo, useRef } from "react";
import { Send, Mail, Smartphone, RotateCw, History, Bell } from "lucide-react";
import toast from "react-hot-toast";
import { AppShell } from "../../components/Layout/AppShell";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { Badge, PlanBadge } from "../../components/ui/Badge";
import { CheckboxField } from "../../components/ui/CheckboxField";
import { PLAN_META } from "../../lib/planMeta";
import { getBroadcastAudience, getBroadcasts, createBroadcast, processBroadcast } from "../../business/AdminBusiness";

const inputClass = "w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent";
const labelClass = "block text-sm font-medium text-gray-700 mb-1";

const TITLE_MAX = 120;
const BODY_MAX = 2000;

// Mesma ordem e chaves do BROADCAST_PLANS do backend (planos efetivos de getEffectivePlan).
// Essencial e teste não estão no PLAN_META porque não aparecem como badge no resto do painel.
const PLAN_OPTIONS = [
  { key: "free", label: PLAN_META.free.label },
  { key: "trial", label: "Em teste (14 dias)" },
  { key: "essencial_individual", label: "Essencial Individual" },
  { key: "essencial_equipe", label: "Essencial Equipe" },
  { key: "start", label: PLAN_META.start.label },
  { key: "pro", label: PLAN_META.pro.label },
  { key: "studio", label: PLAN_META.studio.label },
  { key: "business", label: PLAN_META.business.label },
  { key: "enterprise", label: PLAN_META.enterprise.label },
];
const PLAN_LABELS = Object.fromEntries(PLAN_OPTIONS.map(({ key, label }) => [key, label]));

const toInputDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const daysFromNow = (days) => toInputDate(new Date(Date.now() + days * 24 * 60 * 60 * 1000));

const EMPTY_FORM = {
  title: "",
  body: "",
  email: true,
  push: true,
  bell: false,
  // Último dia em que o aviso aparece no sino (o backend recebe o fim desse dia)
  bellUntil: daysFromNow(14),
  plans: [],
};

const formatDay = (value) => (value ? new Date(value).toLocaleDateString("pt-BR") : "—");

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—";

export const Broadcasts = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [audience, setAudience] = useState({});
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Disparo sendo enviado agora (resumo devolvido pelo backend a cada lote)
  const [sending, setSending] = useState(null);
  const sendingRef = useRef(false);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [audienceRes, historyRes] = await Promise.all([getBroadcastAudience(), getBroadcasts()]);
      setAudience(audienceRes.data?.plans || {});
      setHistory(Array.isArray(historyRes.data) ? historyRes.data : []);
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Erro ao carregar os disparos");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Fechar/recarregar a aba no meio interrompe o envio (dá pra retomar pelo histórico) — avisa.
  useEffect(() => {
    if (!sending) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [sending]);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const togglePlan = (plan, checked) =>
    setForm((prev) => ({
      ...prev,
      plans: checked ? [...prev.plans, plan] : prev.plans.filter((item) => item !== plan),
    }));

  const allPlansSelected = form.plans.length === PLAN_OPTIONS.length;
  const toggleAllPlans = () => setField("plans", allPlansSelected ? [] : PLAN_OPTIONS.map(({ key }) => key));

  const reach = useMemo(() => form.plans.reduce(
    (acc, plan) => ({
      total: acc.total + (audience[plan]?.total || 0),
      email: acc.email + (audience[plan]?.email || 0),
    }),
    { total: 0, email: 0 },
  ), [form.plans, audience]);

  const channelsLabel = [form.email && "e-mail", form.push && "push", form.bell && "aviso no sino"].filter(Boolean).join(" e ");

  const validate = () => {
    if (!form.title.trim() || !form.body.trim()) return "Preencha o título e a mensagem";
    if (!form.email && !form.push && !form.bell) return "Escolha pelo menos um canal";
    if (form.bell && (!form.bellUntil || form.bellUntil < toInputDate(new Date()))) return "Escolha até quando o aviso fica no sino";
    if (!form.plans.length) return "Escolha pelo menos um plano";
    if (!reach.total) return "Nenhuma conta nos planos escolhidos";
    return null;
  };

  const openConfirm = () => {
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }
    setConfirmOpen(true);
  };

  // Chama /process até o backend dizer "done". Cada chamada manda um lote (~200 contas).
  const runBroadcast = async (broadcast) => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    let current = broadcast;
    setSending(current);
    try {
      while (current.status !== "done") {
        const res = await processBroadcast(current._id);
        current = res.data;
        setSending(current);
      }
      const { stats, channels } = current;
      toast.success(
        channels.email || channels.push
          ? `Disparo concluído — ${stats.emailSent} e-mail(s), ${stats.pushSent} push${channels.bell ? " e aviso no sino" : ""}`
          : `Aviso publicado no sino até ${formatDay(current.bellUntil)}`
      );
    } catch (error) {
      toast.error(
        `${error?.response?.data?.msg || "Envio interrompido"}. Dá pra retomar pelo histórico.`,
        { duration: 8000 },
      );
    } finally {
      sendingRef.current = false;
      setSending(null);
      fetchData();
    }
  };

  const handleSend = async () => {
    setConfirmOpen(false);
    try {
      const res = await createBroadcast({
        title: form.title.trim(),
        body: form.body.trim(),
        channels: { email: form.email, push: form.push, bell: form.bell },
        bellUntil: form.bell ? new Date(`${form.bellUntil}T23:59:59`).toISOString() : null,
        plans: form.plans,
      });
      setForm(EMPTY_FORM);
      await runBroadcast(res.data);
    } catch (error) {
      toast.error(error?.response?.data?.msg || "Erro ao criar o disparo");
    }
  };

  const progress = sending?.total ? Math.round((sending.processed / sending.total) * 100) : 0;

  return (
    <>
      <AppShell
        icon={Send}
        eyebrow="Crescimento"
        title="Disparos"
        meta="E-mail, push ou aviso no sino do app para as contas de planos específicos"
      >
          <div className="space-y-6">

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
              <div className="space-y-5 rounded-2xl border border-gray-200/70 bg-white p-5 lg:col-span-3">
                <div className="flex items-center">
                  <Send className="h-5 w-5 text-accent mr-2" />
                  <h3 className="text-base font-semibold text-gray-900">Novo disparo</h3>
                </div>
                <div>
                  <label htmlFor="broadcast-title" className={labelClass}>Título</label>
                  <input
                    id="broadcast-title"
                    className={inputClass}
                    placeholder="Ex.: Manutenção programada hoje às 23h"
                    maxLength={TITLE_MAX}
                    value={form.title}
                    onChange={(e) => setField("title", e.target.value)}
                  />
                  <p className="mt-1 text-xs text-gray-400">Vira o assunto do e-mail, o título do push e o título do aviso no sino. {form.title.length}/{TITLE_MAX}</p>
                </div>

                <div>
                  <label htmlFor="broadcast-body" className={labelClass}>Mensagem</label>
                  <textarea
                    id="broadcast-body"
                    className={`${inputClass} min-h-[160px]`}
                    placeholder="Escreva a mensagem. No e-mail, ela vem depois de “Olá, nome!”."
                    maxLength={BODY_MAX}
                    value={form.body}
                    onChange={(e) => setField("body", e.target.value)}
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    No push, o celular mostra só o começo da mensagem — deixe o essencial nas primeiras linhas. {form.body.length}/{BODY_MAX}
                  </p>
                </div>

                <div>
                  <p className={labelClass}>Canais</p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <CheckboxField
                      id="channel-email"
                      checked={form.email}
                      onChange={(checked) => setField("email", checked)}
                      label="E-mail"
                      help="Contas com “sem disparos” ligado não recebem e-mail."
                    />
                    <CheckboxField
                      id="channel-push"
                      checked={form.push}
                      onChange={(checked) => setField("push", checked)}
                      label="Push no app"
                      help="Chega em quem já fez login no app com notificações ativas."
                    />
                    <CheckboxField
                      id="channel-bell"
                      checked={form.bell}
                      onChange={(checked) => setField("bell", checked)}
                      label="Aviso no sino"
                      help="Fica nas notificações do app e do painel até a data escolhida. A equipe da conta também vê."
                    />
                  </div>
                  {form.bell && (
                    <div className="mt-3 sm:max-w-xs">
                      <label htmlFor="broadcast-bell-until" className={labelClass}>No sino até</label>
                      <input
                        id="broadcast-bell-until"
                        type="date"
                        className={inputClass}
                        min={toInputDate(new Date())}
                        max={daysFromNow(90)}
                        value={form.bellUntil}
                        onChange={(e) => setField("bellUntil", e.target.value)}
                      />
                      <p className="mt-1 text-xs text-gray-400">Some sozinho depois desse dia. Máximo de 90 dias.</p>
                    </div>
                  )}
                </div>

                <div>
                  <div className="mb-1 flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700">Planos</p>
                    <button type="button" onClick={toggleAllPlans} className="text-xs font-medium text-accent hover:underline">
                      {allPlansSelected ? "Limpar seleção" : "Selecionar todos"}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {PLAN_OPTIONS.map(({ key, label }) => (
                      <CheckboxField
                        key={key}
                        id={`plan-${key}`}
                        checked={form.plans.includes(key)}
                        onChange={(checked) => togglePlan(key, checked)}
                        label={label}
                        help={isLoading ? "Carregando..." : `${audience[key]?.total ?? 0} conta(s)`}
                      />
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-gray-600">
                    Vai para <strong className="text-gray-900">{reach.total}</strong> conta(s)
                    {form.email && <> · <strong className="text-gray-900">{reach.email}</strong> com e-mail</>}
                  </p>
                  <button
                    type="button"
                    onClick={openConfirm}
                    disabled={Boolean(sending)}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover disabled:opacity-60"
                  >
                    <Send className="h-4 w-4" /> {form.email || form.push ? "Enviar disparo" : "Publicar aviso"}
                  </button>
                </div>

                {sending && (
                  <div className="rounded-lg border border-accent/30 bg-accent-light p-4">
                    <div className="mb-2 flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-900">Enviando “{sending.title}”...</span>
                      <span className="text-gray-600">{sending.processed}/{sending.total}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-white">
                      <div className="h-full bg-accent transition-all" style={{ width: `${progress}%` }} />
                    </div>
                    <p className="mt-2 text-xs text-gray-500">Não feche esta aba até terminar.</p>
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-gray-200/70 bg-white p-5 lg:col-span-2">
                <div className="flex items-center mb-4">
                  <History className="h-5 w-5 text-accent mr-2" />
                  <h3 className="text-base font-semibold text-gray-900">Histórico</h3>
                </div>
                <div className="divide-y divide-gray-100">
                  {isLoading && !history.length ? (
                    <p className="py-8 text-center text-sm text-gray-400">Carregando...</p>
                  ) : history.length ? history.map((item) => (
                    <div key={item._id} className="space-y-2 py-4 first:pt-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-gray-900">{item.title}</p>
                          <p className="text-xs text-gray-500">{formatDateTime(item.createdAt)} · {item.createdByEmail || "—"}</p>
                        </div>
                        <Badge className={item.status === "done" ? "bg-green-100 text-green-700 normal-case" : "bg-amber-100 text-amber-700 normal-case"}>
                          {item.status === "done" ? "Enviado" : `Parado em ${item.processed}/${item.total}`}
                        </Badge>
                      </div>
                      <p className="line-clamp-2 text-xs text-gray-600 whitespace-pre-line">{item.body}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {item.plans.map((plan) => PLAN_META[plan]
                          ? <PlanBadge key={plan} plan={plan} meta={PLAN_META} />
                          : <Badge key={plan} className="bg-gray-100 text-gray-700 normal-case">{PLAN_LABELS[plan] || plan}</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                        {item.channels.email && (
                          <span className="inline-flex items-center gap-1">
                            <Mail className="h-3.5 w-3.5" /> {item.stats.emailSent} enviado(s)
                            {item.stats.emailFailed > 0 && <span className="text-red-600">· {item.stats.emailFailed} falha(s)</span>}
                          </span>
                        )}
                        {item.channels.push && (
                          <span className="inline-flex items-center gap-1">
                            <Smartphone className="h-3.5 w-3.5" /> {item.stats.pushSent} push
                            {item.stats.pushFailed > 0 && <span className="text-red-600">· {item.stats.pushFailed} falha(s)</span>}
                          </span>
                        )}
                        {item.channels.bell && (
                          <span className="inline-flex items-center gap-1">
                            <Bell className="h-3.5 w-3.5" />
                            {item.bellUntil && new Date(item.bellUntil) > new Date() ? `No sino até ${formatDay(item.bellUntil)}` : "Saiu do sino"}
                          </span>
                        )}
                        {item.status !== "done" && !sending && (
                          <button
                            type="button"
                            onClick={() => runBroadcast(item)}
                            className="inline-flex items-center gap-1 font-medium text-accent hover:underline"
                          >
                            <RotateCw className="h-3.5 w-3.5" /> Retomar
                          </button>
                        )}
                      </div>
                    </div>
                  )) : (
                    <p className="py-8 text-center text-sm text-gray-400">Nenhum disparo enviado ainda</p>
                  )}
                </div>
              </div>
            </div>
          </div>
      </AppShell>

      <ConfirmModal
        isOpen={confirmOpen}
        title="Confirmar disparo"
        message={`Enviar “${form.title.trim()}” por ${channelsLabel} para ${reach.total} conta(s)? Não dá pra desfazer depois de enviado.`}
        confirmLabel="Enviar"
        danger={false}
        onConfirm={handleSend}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};
